/**
 * Chat state: the live socket's status, the rooms the client has open, and
 * the supporter inbox. See docs/rfcs/2026-09-19-realtime-chat-public-private.md.
 *
 * Two feeds write here:
 *   - REST thunks below (history pages, inbox pages, room ids);
 *   - the WebSocket (`useChatSocket`), whose frames map 1:1 onto the
 *     `chat*` reducers (joined, new_message, ticket_*).
 *
 * Messages are kept oldest → newest and de-duplicated by `id`, so a REST
 * page and a live frame for the same message merge cleanly. That is what
 * makes the reconnect contract work: after the socket reconnects
 * (`connectionEpoch` bumps), each held room re-fetches its latest page and
 * it merges with whatever arrived live meanwhile. `syncedEpoch` records
 * which connection a room's latest page was fetched under, so a component
 * remounting later can tell whether its cache is still gap-free. Losing the
 * last holder clears it too: the socket left the room, so the next holder
 * re-fetches just as after a reconnect.
 *
 * Room membership is held by NAMED holders (`roomHolders`), not by mounted
 * components: a feature (e.g. "public-room") holds a room from its explicit
 * join until its explicit leave, surviving unmounts (tab switches, closing
 * the panel). The socket is in a room while it has ≥1 holder — see
 * `useChatSocket`'s joinRoom / leaveRoom.
 *
 * Unread counts: supporters get `unread_count` per inbox room; a user gets
 * `myRoomUnread` for their own ticket (GET /chat/rooms/me, or the
 * creation-free /chat/rooms/me/unread for the rail badge). Read state is per
 * side: support counts only the owner's messages, the owner counts every
 * supporter's. Both go up on a live message from the other side while the
 * room isn't on screen
 * (`viewingRooms`), and drop to 0 on the read signal or a history fetch —
 * the server marks the other side's messages read on either.
 *
 * The inbox is the last fetched page(s) for one filter, patched live by
 * ticket_* frames. A scoped supporter gets `ticket_removed` (room id only)
 * for a ticket that left their scope: it drops out of the list and is
 * remembered in `removedTicketIds` (so an open detail closes) until a frame
 * brings it back into scope, or a first inbox page lists it (the server lists
 * only rooms in scope, so a missed frame can't strand it). When a frame could ADD a room this list doesn't hold, it
 * sets `inbox.stale` so the view re-fetches. "Mine" is decided against
 * `viewerSub`, mirrored from /api/me so these reducers can see it.
 */
import { createAsyncThunk, createSlice, type PayloadAction } from "@reduxjs/toolkit";
import { api } from "@/api/client";
import { fetchMe, saveProfile } from "@/state/slices/meSlice";
import type {
  ChatHistoryResponse,
  ChatInboxFilter,
  ChatInboxResponse,
  ChatInboxRoom,
  ChatMessage,
  ChatMyRoomResponse,
  ChatServerFrame,
  MePayload,
} from "@/api/types";

type LoadStatus = "idle" | "loading" | "succeeded" | "failed";

/** One `ticket_status` claim; `seq` tells repeat claims apart. */
export interface ChatClaim {
  supporterName: string;
  seq: number;
}

export interface ChatRoomState {
  /** Oldest → newest, unique by id. */
  messages: ChatMessage[];
  /** `before_id` for the next older page; null = nothing older. */
  olderCursor: number | null;
  /** `connectionEpoch` the latest page was fetched under; null = never.
   *  Differs from the current epoch ⇒ the socket reconnected since, and
   *  frames from the gap may be missing — re-fetch before trusting it. */
  syncedEpoch: number | null;
  status: LoadStatus;
  loadingOlder: boolean;
  error: string | null;
}

export interface ChatInboxState {
  /** Filter the loaded rooms belong to; null until the first fetch. */
  filter: ChatInboxFilter | null;
  rooms: ChatInboxRoom[];
  nextCursor: string | null;
  status: LoadStatus;
  loadingMore: boolean;
  /** requestId of the "Load more" that `loadingMore` is waiting on, so an
   *  older one finishing late can't clear a newer one's spinner. */
  loadingMoreRequestId: string | null;
  error: string | null;
  /** A live event may have changed which rooms belong here — re-fetch. */
  stale: boolean;
}

interface ChatState {
  /** The signed-in user's `sub` (from /api/me) — "mine" means this. */
  viewerSub: string;
  /** True while the WebSocket is open. */
  socketConnected: boolean;
  /** Bumps on every successful (re)connect. Open rooms re-fetch their
   *  latest history when it changes (the socket replays nothing). */
  connectionEpoch: number;
  /** Rooms the server confirmed (`joined`) on the current socket. */
  joinedRoomIds: string[];
  /** roomId → the features holding it (e.g. "public-room"). Survives
   *  unmounts and reconnects; only explicit leaves (or a refused join)
   *  remove holders. */
  roomHolders: Record<string, string[]>;
  /** roomId → how many components are displaying its thread right now
   *  (recorded by `useRoomViewing`, the one "on screen" signal). A count,
   *  not a flag: two views of one room — or StrictMode's remount — must not
   *  clear each other. Joined ≠ on screen: holds outlive the UI. */
  viewingRooms: Record<string, number>;
  myRoomId: string | null;
  /** The user's unread count in their own room (their chat badge). */
  myRoomUnread: number;
  publicRoomId: string | null;
  rooms: Record<string, ChatRoomState>;
  inbox: ChatInboxState;
  /** `ticket_status` — the supporter now handling a room, for the user's
   *  "Support is now looking at your message" banner. */
  claimedBy: Record<string, ChatClaim>;
  /** Monotonic counter behind `ChatClaim.seq`. */
  claimSeq: number;
  /** Bumps on every ticket_* frame (incoming / claimed / closed /
   *  unclaimed), whether or not it changed the loaded inbox — so views can
   *  refresh things the slice doesn't hold, like the inbox counts. */
  ticketEventSeq: number;
  /** Tickets that left this (scoped) supporter's scope (`ticket_removed`),
   *  until a later frame shows them in scope again. */
  removedTicketIds: string[];
}

const initialInbox: ChatInboxState = {
  filter: null,
  rooms: [],
  nextCursor: null,
  status: "idle",
  loadingMore: false,
  loadingMoreRequestId: null,
  error: null,
  stale: false,
};

const initialState: ChatState = {
  viewerSub: "",
  socketConnected: false,
  connectionEpoch: 0,
  joinedRoomIds: [],
  roomHolders: {},
  viewingRooms: {},
  myRoomId: null,
  myRoomUnread: 0,
  publicRoomId: null,
  rooms: {},
  inbox: initialInbox,
  claimedBy: {},
  claimSeq: 0,
  ticketEventSeq: 0,
  removedTicketIds: [],
};

function emptyRoom(): ChatRoomState {
  return {
    messages: [],
    olderCursor: null,
    syncedEpoch: null,
    status: "idle",
    loadingOlder: false,
    error: null,
  };
}

function roomOf(state: ChatState, roomId: string): ChatRoomState {
  if (!state.rooms[roomId]) state.rooms[roomId] = emptyRoom();
  return state.rooms[roomId];
}

/** Nobody holds `roomId` any more, so the socket is out of it and its live
 *  frames stop arriving. The cached page can't vouch for anything sent from
 *  now on: clearing `syncedEpoch` makes the next holder re-fetch the latest
 *  page, exactly as after a reconnect. The cached messages stay (they merge
 *  with that page). */
function unholdRoom(state: ChatState, roomId: string): void {
  delete state.roomHolders[roomId];
  const room = state.rooms[roomId];
  if (room) room.syncedEpoch = null;
}

/** Merge by id, ascending. Later copies win (a REST row carries is_read). */
function mergeMessages(a: ChatMessage[], b: ChatMessage[]): ChatMessage[] {
  const byId = new Map<number, ChatMessage>();
  for (const m of a) byId.set(m.id, m);
  for (const m of b) byId.set(m.id, { ...byId.get(m.id), ...m });
  return [...byId.values()].sort((x, y) => x.id - y.id);
}

/** A "Load more" request finished (adopted, dropped or failed). Clears the
 *  spinner only if it was that request's: a newer Load more owns it now. */
function endLoadMore(inbox: ChatInboxState, requestId: string): void {
  if (inbox.loadingMoreRequestId !== requestId) return;
  inbox.loadingMore = false;
  inbox.loadingMoreRequestId = null;
}

/** Whether a room belongs under `filter`; undefined only for "mine" before
 *  /api/me has told us who the viewer is. */
function matchesFilter(
  room: ChatInboxRoom,
  filter: ChatInboxFilter,
  viewerSub: string,
): boolean | undefined {
  switch (filter) {
    case "unclaimed":
      return room.status === "open" && room.assigned_supporter_id === null;
    case "closed":
      return room.status === "closed";
    case "all":
      return true;
    case "mine":
      if (room.status !== "open") return false;
      return viewerSub ? room.assigned_supporter_id === viewerSub : undefined;
  }
}

/** Apply `patch` to the inbox row for `roomId` (if loaded), then drop it or
 *  flag the list stale if it no longer clearly belongs. Returns whether the
 *  row was loaded. */
/** A full ticket_* frame for a room proves it is in scope again. */
function backInScope(state: ChatState, roomId: string): void {
  if (state.removedTicketIds.includes(roomId)) {
    state.removedTicketIds = state.removedTicketIds.filter((id) => id !== roomId);
  }
}

function patchInboxRoom(
  inbox: ChatInboxState,
  viewerSub: string,
  roomId: string,
  patch: (room: ChatInboxRoom) => void,
): boolean {
  const idx = inbox.rooms.findIndex((r) => r.id === roomId);
  if (idx < 0 || inbox.filter === null) return false;
  const room = inbox.rooms[idx];
  patch(room);
  const belongs = matchesFilter(room, inbox.filter, viewerSub);
  if (belongs === false) inbox.rooms.splice(idx, 1);
  else if (belongs === undefined) inbox.stale = true;
  return true;
}

// --- thunks ---

export const fetchMyChatRoom = createAsyncThunk<ChatMyRoomResponse, void>(
  "chat/fetchMyRoom",
  async () => api.chatMyRoom(),
);

/** The rail badge: the user's unread count without creating their room. */
export const fetchMyChatUnread = createAsyncThunk<
  { room_id: string | null; unread_count: number },
  void
>("chat/fetchMyUnread", async () => api.chatMyUnread());

export const fetchPublicChatRoom = createAsyncThunk<string, void>(
  "chat/fetchPublicRoom",
  async () => (await api.chatPublicRoom()).room_id,
);

/** Latest page (no `beforeId`) or the next older page. The connection
 *  epoch at request time rides along, so a reconnect that lands mid-request
 *  doesn't mark the page as covering the new connection. */
export const fetchChatHistory = createAsyncThunk<
  ChatHistoryResponse & { epoch?: number },
  { roomId: string; beforeId?: number }
>("chat/fetchHistory", async ({ roomId, beforeId }, { getState }) => {
  const epoch = (getState() as { chat: ChatState }).chat.connectionEpoch;
  return { ...(await api.chatHistory(roomId, beforeId)), epoch };
});

/** The "I'm viewing this room" signal (RFC read-marking note): marks the
 *  other side's messages read via POST /chat/rooms/{id}/read, which returns
 *  no messages. Sent whenever a thread is displayed, even when the cached
 *  history is fresh — so it is deliberately independent of
 *  fetchChatHistory / useHeldRoom, and changes no cached messages.
 *  Marks only up to the newest message the thread holds (what it is
 *  showing), so one committed after that — not yet delivered — stays
 *  unread; an empty thread marks nothing. */
export const markChatRoomRead = createAsyncThunk<void, string>(
  "chat/markRead",
  async (roomId, { getState }) => {
    const messages = (getState() as { chat: ChatState }).chat.rooms[roomId]?.messages ?? [];
    await api.chatMarkRead(roomId, messages[messages.length - 1]?.id ?? 0);
  },
);

/** First page for `filter` (no `cursor`) or the next page. */
export const fetchChatInbox = createAsyncThunk<
  ChatInboxResponse,
  { filter: ChatInboxFilter; cursor?: string }
>("chat/fetchInbox", async ({ filter, cursor }) =>
  api.chatInbox(filter, cursor),
);

type TicketFrame<T extends ChatServerFrame["type"]> = Omit<
  Extract<ChatServerFrame, { type: T }>,
  "type"
>;

const chatSlice = createSlice({
  name: "chat",
  initialState,
  reducers: {
    chatSocketOpened: (state) => {
      state.socketConnected = true;
      state.connectionEpoch += 1;
      // A fresh socket starts in no rooms; the hook re-joins them.
      state.joinedRoomIds = [];
    },
    chatSocketClosed: (state) => {
      state.socketConnected = false;
      state.joinedRoomIds = [];
    },
    chatRoomJoined: (state, action: PayloadAction<string>) => {
      if (!state.joinedRoomIds.includes(action.payload)) {
        state.joinedRoomIds.push(action.payload);
      }
    },
    chatRoomLeft: (state, action: PayloadAction<string>) => {
      state.joinedRoomIds = state.joinedRoomIds.filter((id) => id !== action.payload);
    },
    chatRoomHeld: (state, action: PayloadAction<{ roomId: string; holder: string }>) => {
      const { roomId, holder } = action.payload;
      const holders = state.roomHolders[roomId] ?? [];
      if (!holders.includes(holder)) state.roomHolders[roomId] = [...holders, holder];
    },
    chatRoomReleased: (state, action: PayloadAction<{ roomId: string; holder: string }>) => {
      const { roomId, holder } = action.payload;
      const rest = (state.roomHolders[roomId] ?? []).filter((h) => h !== holder);
      if (rest.length) state.roomHolders[roomId] = rest;
      else unholdRoom(state, roomId);
    },
    chatRoomViewingStarted: (state, action: PayloadAction<string>) => {
      state.viewingRooms[action.payload] = (state.viewingRooms[action.payload] ?? 0) + 1;
    },
    chatRoomViewingStopped: (state, action: PayloadAction<string>) => {
      const left = (state.viewingRooms[action.payload] ?? 0) - 1;
      if (left > 0) state.viewingRooms[action.payload] = left;
      else delete state.viewingRooms[action.payload];
    },
    /** The server refused the join: nobody holds the room any more. */
    chatRoomRefused: (state, action: PayloadAction<string>) => {
      unholdRoom(state, action.payload);
    },
    chatMessageReceived: (state, action: PayloadAction<ChatMessage>) => {
      const { id, room_id, sender_id } = action.payload;
      const room = roomOf(state, room_id);
      const isNew = !room.messages.some((m) => m.id === id);
      room.messages = mergeMessages(room.messages, [action.payload]);
      // A reply to the user's own ticket that nobody is looking at: the same
      // on-screen rule as ticket_incoming's inbox bump.
      if (
        isNew &&
        room_id === state.myRoomId &&
        sender_id !== state.viewerSub &&
        !((state.viewingRooms[room_id] ?? 0) > 0)
      ) {
        state.myRoomUnread += 1;
      }
    },
    chatTicketIncoming: (state, action: PayloadAction<TicketFrame<"ticket_incoming">>) => {
      state.ticketEventSeq += 1;
      const { room_id, user_id, user_name, sender_id, preview } = action.payload;
      backInScope(state, room_id);
      // Read state is per side: for support, only the owner's messages are
      // unread — never another supporter's reply. And only a room on screen
      // counts as seen; a joined-but-hidden one (a hold outliving its UI)
      // still gets the unread bump.
      const fromOwner = sender_id === user_id;
      const seenLive = (state.viewingRooms[room_id] ?? 0) > 0;
      const inbox = state.inbox;
      const loaded = patchInboxRoom(inbox, state.viewerSub, room_id, (room) => {
        room.status = "open";
        room.assigned_supporter_id = null;
        room.preview = preview;
        room.user_name = user_name;
        room.is_stale = false;
        if (fromOwner && !seenLive) room.unread_count += 1;
      });
      if (loaded) {
        // Newest activity sorts first.
        const idx = inbox.rooms.findIndex((r) => r.id === room_id);
        if (idx > 0) inbox.rooms.unshift(...inbox.rooms.splice(idx, 1));
      } else if (inbox.filter === "unclaimed" || inbox.filter === "all") {
        inbox.stale = true;
      }
    },
    chatTicketClaimed: (state, action: PayloadAction<TicketFrame<"ticket_claimed">>) => {
      state.ticketEventSeq += 1;
      const { room_id, supporter_id } = action.payload;
      backInScope(state, room_id);
      const loaded = patchInboxRoom(state.inbox, state.viewerSub, room_id, (room) => {
        room.assigned_supporter_id = supporter_id;
        room.status = "open";
      });
      // Claimed by me → it now belongs in "Mine" but isn't loaded.
      if (!loaded && state.inbox.filter === "mine" && supporter_id === state.viewerSub) {
        state.inbox.stale = true;
      }
    },
    chatTicketClosed: (state, action: PayloadAction<TicketFrame<"ticket_closed">>) => {
      state.ticketEventSeq += 1;
      backInScope(state, action.payload.room_id);
      const loaded = patchInboxRoom(state.inbox, state.viewerSub, action.payload.room_id, (room) => {
        room.status = "closed";
      });
      if (!loaded && state.inbox.filter === "closed") state.inbox.stale = true;
    },
    chatTicketUnclaimed: (state, action: PayloadAction<TicketFrame<"ticket_unclaimed">>) => {
      state.ticketEventSeq += 1;
      backInScope(state, action.payload.room_id);
      const loaded = patchInboxRoom(state.inbox, state.viewerSub, action.payload.room_id, (room) => {
        room.assigned_supporter_id = null;
      });
      if (!loaded && state.inbox.filter === "unclaimed") state.inbox.stale = true;
    },
    /** The ticket left this supporter's scope: out of every list, now. */
    chatTicketRemoved: (state, action: PayloadAction<TicketFrame<"ticket_removed">>) => {
      state.ticketEventSeq += 1;
      const { room_id } = action.payload;
      state.inbox.rooms = state.inbox.rooms.filter((r) => r.id !== room_id);
      if (!state.removedTicketIds.includes(room_id)) state.removedTicketIds.push(room_id);
    },
    chatTicketStatus: (state, action: PayloadAction<TicketFrame<"ticket_status">>) => {
      // A fresh seq per event, so a re-claim by the same supporter still
      // reads as a new claim.
      state.claimSeq += 1;
      state.claimedBy[action.payload.room_id] = {
        supporterName: action.payload.supporter_name,
        seq: state.claimSeq,
      };
    },
  },
  extraReducers: (builder) => {
    const adoptViewer = (state: ChatState, p: MePayload) => {
      state.viewerSub = p.authenticated ? p.sub ?? "" : "";
    };
    builder
      .addCase(fetchMe.fulfilled, (state, action) => adoptViewer(state, action.payload))
      .addCase(saveProfile.fulfilled, (state, action) => adoptViewer(state, action.payload))
      .addCase(fetchMyChatRoom.fulfilled, (state, action) => {
        state.myRoomId = action.payload.room_id;
        state.myRoomUnread = action.payload.unread_count;
      })
      .addCase(fetchMyChatUnread.fulfilled, (state, action) => {
        const { room_id, unread_count } = action.payload;
        // "No room yet" never overrides a room this session already knows.
        if (room_id === null) {
          if (state.myRoomId === null) state.myRoomUnread = 0;
          return;
        }
        state.myRoomId = room_id;
        // On screen, the thread is being read: don't resurrect a count the
        // read signal is about to clear.
        state.myRoomUnread = (state.viewingRooms[room_id] ?? 0) > 0 ? 0 : unread_count;
      })
      .addCase(fetchPublicChatRoom.fulfilled, (state, action) => {
        state.publicRoomId = action.payload;
      })
      .addCase(fetchChatHistory.pending, (state, action) => {
        const room = roomOf(state, action.meta.arg.roomId);
        if (action.meta.arg.beforeId != null) room.loadingOlder = true;
        else room.status = "loading";
        room.error = null;
      })
      .addCase(fetchChatHistory.fulfilled, (state, action) => {
        const { roomId, beforeId } = action.meta.arg;
        const room = roomOf(state, roomId);
        const page = [...action.payload.messages].reverse();
        if (beforeId != null) {
          room.messages = mergeMessages(page, room.messages);
          room.olderCursor = action.payload.next_cursor;
          room.loadingOlder = false;
        } else {
          const newestKnown = room.messages[room.messages.length - 1]?.id ?? 0;
          const leavesGap =
            action.payload.next_cursor !== null &&
            page.length > 0 &&
            page[0].id > newestKnown;
          if (room.messages.length === 0 || leavesGap) {
            // First load, or more arrived while away than one page covers:
            // the old tail isn't contiguous with this page, so start over.
            room.messages = page;
            room.olderCursor = action.payload.next_cursor;
          } else {
            room.messages = mergeMessages(room.messages, page);
          }
          room.status = "succeeded";
          // Released while in flight: the socket left, so this page vouches
          // for nothing past its own arrival (see unholdRoom).
          room.syncedEpoch = state.roomHolders[roomId]
            ? action.payload.epoch ?? state.connectionEpoch
            : null;
        }
        // The server marked the other side's messages read on this fetch.
        const inboxRoom = state.inbox.rooms.find((r) => r.id === roomId);
        if (inboxRoom) inboxRoom.unread_count = 0;
        if (roomId === state.myRoomId) state.myRoomUnread = 0;
      })
      .addCase(fetchChatHistory.rejected, (state, action) => {
        const room = roomOf(state, action.meta.arg.roomId);
        if (action.meta.arg.beforeId != null) room.loadingOlder = false;
        else room.status = "failed";
        room.error = action.error.message ?? "Failed to load messages";
      })
      .addCase(markChatRoomRead.fulfilled, (state, action) => {
        const roomId = action.meta.arg;
        const inboxRoom = state.inbox.rooms.find((r) => r.id === roomId);
        if (inboxRoom) inboxRoom.unread_count = 0;
        if (roomId === state.myRoomId) state.myRoomUnread = 0;
      })
      .addCase(fetchChatInbox.pending, (state, action) => {
        if (action.meta.arg.cursor) {
          state.inbox.loadingMore = true;
          state.inbox.loadingMoreRequestId = action.meta.requestId;
        } else state.inbox.status = "loading";
        state.inbox.error = null;
      })
      .addCase(fetchChatInbox.fulfilled, (state, action) => {
        const { filter, cursor } = action.meta.arg;
        const inbox = state.inbox;
        if (cursor) {
          endLoadMore(inbox, action.meta.requestId);
          // `filter` only changes when a first page lands, so this drops
          // only a later page of a filter the user has since left — never
          // the first page of the one they switched to (no cursor).
          if (inbox.filter !== filter) return;
          const have = new Set(inbox.rooms.map((r) => r.id));
          inbox.rooms.push(...action.payload.rooms.filter((r) => !have.has(r.id)));
        } else {
          inbox.filter = filter;
          inbox.rooms = action.payload.rooms;
          inbox.stale = false;
          inbox.status = "succeeded";
          // The server only lists rooms the viewer may access, so a listed
          // room is back in scope even if its ticket_* frame was missed.
          for (const room of action.payload.rooms) backInScope(state, room.id);
        }
        inbox.nextCursor = action.payload.next_cursor;
      })
      .addCase(fetchChatInbox.rejected, (state, action) => {
        if (action.meta.arg.cursor) endLoadMore(state.inbox, action.meta.requestId);
        else state.inbox.status = "failed";
        state.inbox.error = action.error.message ?? "Failed to load the inbox";
      });
  },
});

export const {
  chatSocketOpened,
  chatSocketClosed,
  chatRoomJoined,
  chatRoomLeft,
  chatRoomHeld,
  chatRoomReleased,
  chatRoomRefused,
  chatRoomViewingStarted,
  chatRoomViewingStopped,
  chatMessageReceived,
  chatTicketIncoming,
  chatTicketClaimed,
  chatTicketClosed,
  chatTicketUnclaimed,
  chatTicketRemoved,
  chatTicketStatus,
} = chatSlice.actions;
export default chatSlice.reducer;
